import React, { useState, useEffect, useLayoutEffect, memo, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import MidLayout from "./midLayout";
import RightLayout from "./rightLayout";
import ListImage from "./listmage";
import { getUser } from "../../Model/LocalStorage";
import { auctionsLive, auctionsLiveDeclarator } from "../../Model/Auction";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { getToken } from "../../Model/User";
import { GetApprovedAuction } from "../../Model/Auction";
import { R, roomRootSx, thinScrollSx } from "./components/roomTheme";

/**
 * Height available below the site header (UI only).
 * Measured before paint, then again (one animation frame later) whenever the window or the
 * page layout changes — so a header that changes height at a breakpoint is picked up too.
 */
function useAvailableHeight(ref) {
  const [height, setHeight] = useState(null);
  useLayoutEffect(() => {
    let frame = 0;
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY; // header / layout above the room
      const next = Math.max(0, Math.round(window.innerHeight - top));
      setHeight((prev) => (prev === next ? prev : next));
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(measure);
      });
    };
    measure();
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    if (observer) observer.observe(document.documentElement);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      if (observer) observer.disconnect();
    };
  }, [ref]);
  return height;
}

/*
 * Auction room layout and live-data controller.
 *
 * UNCHANGED: every hook below — the LiveAuction fetch (auctionsLive / auctionsLiveDeclarator),
 * the socket listeners ("receive_status", "receive_bid_amount", "refreshClient", connect /
 * reconnect / disconnect), "ConnectedUser" / "RequestLatestAuction" emits, the approval
 * check (GetApprovedAuction) and the visibility-change resync. Only obsolete commented-out
 * code and unused imports were removed.
 *
 * LAYOUT (UI only) — sized to the viewport so the bid action is always visible:
 *  - The room's height is measured as (window height − distance from the top of the page),
 *    i.e. the space left under the site header, and kept up to date on resize.
 *  - Desktop (≥ 900px, same switch as before):
 *      ≥ 1200px  three columns: artwork (largest) | lot information (lot, details,
 *                starting bid, bid history, the auction's lots) | bidding panel.
 *      900–1199  artwork with the lot information below it | bidding panel (sticky).
 *    The bidding panel shows the status / current bid / your bid state from rightLayout
 *    (`currentSlot`) and the next bid + Place Bid control from midLayout (`bidSlot`) —
 *    each keeps its own handlers; they are only displayed there through React portals.
 *  - Phones (< 600px): artwork, compact lot + current / next bid, bid bar fixed at the
 *    bottom. Tablets in portrait (600–899px): artwork | lot + prices side by side.
 */

function App({ Socket, LiveData, mediadQuery, Type }) {
  const [loading] = useState(false);
  const [room, setRoomId] = useState("");
  const [getData, setGetData] = useState("");
  const [auctionTitle, setAuctionTitle] = useState();
  const token = getToken();
  const [, setEmail] = useState("");
  const [lotNumber, setLotNumber] = useState(null);
  const [group, setGroup] = useState("");
  const [toggleValue, setToggleValue] = useState(false);
  const [guest, setGuest] = useState("");
  const [status, setStatus] = useState("");
  const [isSocketConnected, setIsSocketConnected] = useState(() => Boolean(Socket?.connected));
  const [user] = useState(null);
    // Flag para sa Approval Check para isang beses lang i-call
  const [isApprovedChecked, setIsApprovedChecked] = useState(false);

  // STABLE LIVE AUCTION FUNCTION
  const groupRef = useRef(group);
useEffect(() => {
  groupRef.current = group;
}, [group]);


const throttle = (fn, delay) => {
  let last = 0;
  return (...args) => {
    const now = Date.now();
    if (now - last >= delay) {
      last = now;
      fn(...args);
    }
  };
};

const throttledLiveAuction = useRef(throttle(() => LiveAuction(group), 1500)).current;

const connectSocket = useCallback(() => {
  if (!Socket || !user) return;

  // Auto-connect if disconnected
  if (!Socket.connected) Socket.connect();

  // Emit connected user info
  Socket.emit("ConnectedUser", {
    userId: user.id,
    email: user.email,
    group: user.group,
    mobile: Type,
  });

  Socket.emit("RequestLatestAuction", { group: user.group });
}, [Socket, user, Type]);


// eto ung para sa kahit nd naka login or walang account makakapagview sa mga realtime bid
const LiveAuction = useCallback(async (forcedGroup) => {
  // 1. Kunin ang role pero huwag i-block kung empty (pwedeng null/undefined para sa guest)
  const targetGroup = forcedGroup || groupRef.current;

  try {
    // 2. Logic: ADMIN lang ang may special API, lahat ng iba (User o Guest) ay sa auctionsLive()
    const results = (targetGroup === "ADMIN") 
      ? await auctionsLiveDeclarator() 
      : await auctionsLive();

    // 3. I-check kung may nakuha tayong data
    if (results?.success !== false) {
      setGetData(results);
      
      // Safety check: Siguraduhin na may laman ang auction_details bago i-set
      if (results.auction_details) {
        setAuctionTitle(results.auction_details.auction_name);
        setRoomId(results.auction_details.id);
      }

      // Safety check para sa lot number
      if (results.lot?.details?.[0]) {
        setLotNumber(parseInt(results.lot.details[0].lot_number));
      }
    }
  } catch (error) {
    // 4. Mas malinis na error handling para hindi nakakalito sa console
    console.error("LiveAuction fetch failed:", error);
  }
}, []);

  // 1. INITIAL USER & SOCKET SETUP
  useEffect(() => {
    const init = async () => {
      const getuser = await getUser();
      if (getuser) {
        setEmail(getuser.email);
        setGroup(getuser.group);
        LiveAuction(getuser.group);
        Socket.emit("ConnectedUser", {
          userId: getuser.id,
          email: getuser.email,
          group: getuser.group,
          mobile: Type,
        });
      }
    };
    init();
  }, [Socket, Type]); // Run only once or when Type changes

  useEffect(() => {
    const handleRefreshClient = ({ message }) => {
      if (message === "refresh") {
        window.location.reload(); // full page reload para reconnect lahat ng sockets
      }
    };
  
    Socket.on("refreshClient", handleRefreshClient);
  
    return () => {
      Socket.off("refreshClient", handleRefreshClient);
    };
  }, [Socket]);
  

  

  // 2. REAL-TIME BIDDING & STATUS ENGINE (THE FAST PART)
  useEffect(() => {
    if (!Socket) return;
  
    const handleStatus = (data) => {
      const { message } = data;
    
      if (message === "NEXTSLOT" || message === "PASS") {
        setStatus("PENDING");
        setIsApprovedChecked(false);
        LiveAuction();
        return;
      }
    
      if (message === "undo") {
        setIsApprovedChecked(false);

        LiveAuction();
        return;
      }
    
      setStatus(message);
    };
    
  
    const handleBidAmount = (data) => {
      // Isang tawag lang dahil stable na ang LiveAuction reference
      LiveAuction();
    };
  
    // CLEANUP AND SET
    Socket.off("receive_status", handleStatus);
    Socket.off("receive_bid_amount", handleBidAmount);
    
    Socket.on("receive_status", handleStatus);
    Socket.on("receive_bid_amount", handleBidAmount);
  
    return () => {
      Socket.off("receive_status", handleStatus);
      Socket.off("receive_bid_amount", handleBidAmount);
    };
  }, [Socket, LiveAuction]);

  // 3. APPROVAL CHECK - INISOLATE PARA HINDI MA-CALL TUWING MAGBIBID
  useEffect(() => {
    const checkUserApproval = async () => {
      const currentAuctionId = getData?.auction_details?.auction_id || LiveData?.auction_details?.auction_id;
      
      // CALL ONLY IF: may token, may auction ID, at HINDI PA na-check
      if (!token || !currentAuctionId || isApprovedChecked) return;

      try {
        const approvedRequest = await GetApprovedAuction(currentAuctionId);
        if ((approvedRequest && approvedRequest.status === 0) || approvedRequest.status === "declined") {
          setGuest(approvedRequest.status);
        } else {
          setGuest(approvedRequest.msg || "");
        }
        setIsApprovedChecked(true); // LOCK: Hindi na ito matatawag sa susunod na bid
      } catch (error) {
        console.error("Approval Check Error:", error);
      }
    };
    checkUserApproval();
  }, [getData, LiveData, token, isApprovedChecked]);

  // 4. VISIBILITY CHANGE (FOR MOBILE SLEEP MODE)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        connectSocket();      // reconnect kung na-disconnect habang sleep
        throttledLiveAuction();    // fetch latest auction safely
      }
    };
  
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [connectSocket, LiveAuction, group]);
  

  const displayData = getData === "" ? LiveData : getData;

  


  useEffect(() => {
    if (!Socket) return;

    const handleConnect = () => {
      setIsSocketConnected(true);
      connectSocket();
    };
    const handleReconnect = () => {
      setIsSocketConnected(true);
      connectSocket();
    };
    const handleDisconnect = () => {
      setIsSocketConnected(false);
      setTimeout(() => {
        if (!Socket.connected) connectSocket();
      }, 3000);
    };

    setIsSocketConnected(Boolean(Socket.connected));
    Socket.on("connect", handleConnect);
    Socket.on("reconnect", handleReconnect);
    Socket.on("disconnect", handleDisconnect);

    return () => {
      Socket.off("connect", handleConnect);
      Socket.off("reconnect", handleReconnect);
      Socket.off("disconnect", handleDisconnect);
    };
  }, [Socket, connectSocket]);
  // UI-only layout refs. The auction/socket state above remains unchanged.
  const roomRef = useRef(null);
  const fitHeight = useAvailableHeight(roomRef);
const [bidSlot, setBidSlot] = useState(null);
const [currentSlot, setCurrentSlot] = useState(null);
  const isDesktop = mediadQuery.phoneMatch === true;
  const PAD = 12;
  const desktopRoomHeight = fitHeight ? Math.max(0, fitHeight - PAD * 2) : 640;
  const artworkHeight = fitHeight
    ? Math.min(760, Math.max(0, desktopRoomHeight - 24))
    : 560;

  const mobileArtworkHeight = fitHeight
    ? {
        xs: `${Math.min(360, Math.max(180, fitHeight - 420))}px`,
        sm: `${Math.min(500, Math.max(260, fitHeight - 140))}px`,
      }
    : { xs: "clamp(210px, 39svh, 390px)", sm: "clamp(260px, 52svh, 500px)" };

  return (
    <Box
      ref={roomRef}
      className="noScroll"
      aria-busy={!isSocketConnected}
      sx={{
        ...roomRootSx,
        flexGrow: 1,
        overflow: status === "PENDING" || status === "PAUSE" ? "hidden" : "auto",
        overflowX: "hidden",
        height: fitHeight
          ? `${fitHeight}px`
          : "100%",
        maxHeight: "100%",
        minHeight: 0,
        bgcolor: isDesktop ? R.soft : R.paper,
      }}
    >
      {!isDesktop ? (
        /* Phones and tablets: artwork first, then the compact lot/bidding information. */
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "minmax(0, 1.08fr) minmax(320px, 0.92fr)",
            },
            alignItems: "start",
            gap: { xs: 0, sm: 2 },
            px: { sm: 2 },
            pt: { sm: 1.5 },
            maxWidth: 1200,
            mx: "auto",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={{ minWidth: 0, position: { sm: "sticky" }, top: { sm: 12 } }}>
         <MidLayout
  toggleValue={toggleValue}
  setToggleValue={setToggleValue}
  socket={Socket}
  mediadQuery={mediadQuery.phoneMatch}
  titleAuction={auctionTitle}
  room={room}
  details={displayData}
  guest={guest}
  status={status}
  bidSlot={bidSlot}
  artworkHeight={artworkHeight}
/>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <RightLayout
              setToggleValue={setToggleValue}
              mediadQuery={mediadQuery.phoneMatch}
              socket={Socket}
              details={displayData}
              room={room}
              guest={guest}
              status={status}
              isSocketConnected={isSocketConnected}
            />
          </Box>
        </Box>
      ) : (
        /*
         * Desktop auction room:
         * LOTS | ARTWORK | LOT DETAILS + BIDDING
         *
         * The right panel is the action area. The lot list remains independent so a long
         * bid history or catalogue description can never push the artwork or bid controls
         * around. Existing bid controls are still rendered through their original portals.
         */
        <Box
          sx={{
            height: desktopRoomHeight,
            maxWidth: 1920,
            mx: "auto",
            p: `${PAD}px`,
            boxSizing: "border-box",
            display: "grid",
            gap: `${PAD}px`,
            gridTemplateColumns: {
              md: "minmax(0, 1fr) 360px",
              lg: "280px minmax(0, 1fr) 380px",
              xl: "310px minmax(0, 1fr) 410px",
            },
            gridTemplateRows: {
              md: "minmax(0, 1fr) auto",
              lg: "minmax(0, 1fr)",
            },
            gridTemplateAreas: {
              md: '"stage bid" "lots bid"',
              lg: '"lots stage bid"',
            },
            minHeight: 0,
          }}
        >
          {/* Auction lots / navigation */}
          <Box
            component="aside"
            aria-label="Auction lots"
            sx={{
              gridArea: "lots",
              minWidth: 0,
              minHeight: 0,
              bgcolor: R.paper,
              border: `1px solid ${R.line}`,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", ...thinScrollSx }}>
              <ListImage
                socket={Socket}
                details={displayData}
                lotNumber={lotNumber}
                mediadQuery={mediadQuery.phoneMatch}
              />
            </Box>
          </Box>

          {/* Artwork — intentionally kept visually clean: no duplicate auction title above it. */}
          <Box
            component="main"
            aria-label="Current artwork"
            sx={{
              gridArea: "stage",
              minWidth: 0,
              minHeight: 0,
              bgcolor: R.paper,
              border: `1px solid ${R.line}`,
              p: { md: 1, lg: 1.5 },
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <MidLayout
              toggleValue={toggleValue}
              socket={Socket}
              mediadQuery={mediadQuery.phoneMatch}
              titleAuction={auctionTitle}
              room={room}
              details={displayData}
              guest={guest}
              status={status}
              bidSlot={bidSlot}
              artworkHeight={artworkHeight}
            />
          </Box>

          {/* Right action panel: lot details, current bid, bid history and bid action. */}
          <Box
            component="aside"
            aria-label="Current lot and bidding"
            sx={{
              gridArea: "bid",
              minWidth: 0,
              minHeight: 0,
              height: "100%",
              bgcolor: R.paper,
              border: `1px solid ${R.line}`,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 1px 3px rgba(0,0,0,0.035)",
            }}
          >
            <Box
              sx={{
                minHeight: 0,
                flex: 1,
                overflowY: "auto",
                ...thinScrollSx,
              }}
            >
              <RightLayout
                setToggleValue={setToggleValue}
                  mediadQuery={mediadQuery.phoneMatch}
                socket={Socket}
                details={displayData}
                room={room}
                guest={guest}
                status={status}
                currentSlot={currentSlot}
                isSocketConnected={isSocketConnected}
              />
            </Box>

                <Box
                sx={{
                  flexShrink: 0,
                  borderTop: `1px solid ${R.lineStrong}`,
                  bgcolor: R.paper,
                  p: 1.25,
                }}
              >
                {/* CURRENT BID */}
                <Box
                  ref={setCurrentSlot}
                  sx={{
                    minWidth: 0,
                    width: "100%",
                  }}
                />

                {/* FULL WIDTH PLACE BID BUTTON */}
                <Box
                  ref={setBidSlot}
                  sx={{
                    width: "100%",
                    mt: 1,
                  }}
                />
              </Box>



          </Box>
        </Box>
      )}
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}

export default memo(App);
